import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import TimeSlotsContainer from "components/BusinessAvailabilityDisplay/TimeSlotsContainer";
import { DateTime } from "luxon";
import Link from "next/link";
import Button from "components/Button";

const DesktopAvailabilityWrapper = styled.div`
  display: none;

  ${({ theme }) => theme.mq.lg`
    display: flex;
    height: ${(props) => (props.isVeterinary && "2rem") || "auto"};
    width: 100%;
  `}
`;

const MobileAvailabilityWrapper = styled.div`
  width: 100%;

  ${({ theme }) => theme.mq.lg`
    display: none;
  `}
`;

const EmptyAvailabilityButton = styled.button`
  background-color: transparent;
  color: ${({ theme }) => theme.themeColors.secondary};
  border: 0.0625rem solid ${({ theme }) => theme.themeColors.secondary};
  border-radius: 1.3125rem;
  display: flex;
  font-size: 0.75rem;
  height: 2rem;
  justify-content: center;
  margin-top: 1rem;
  padding: 0.5rem;
  width: 100%;
  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 0.125rem solid #0070f3;
  }

  :hover {
    color: ${({ theme }) => theme.themeColors.goodfriendDark};
    border: 0.0625rem solid ${({ theme }) => theme.themeColors.goodfriendDark};
  }

  ${({ theme }) => theme.mq.lg`
    margin-top: 0;
    width: 23.125rem;
  `}
`;

const EmptyAvailabilityPill = styled.div`
  background-color: ${({ theme }) =>
    theme.themeColors.hoverSecondaryPoint2Opacity};
  color: ${({ theme }) => theme.themeColors.secondary};
  border: none;
  border-radius: 6px;
  display: flex;
  font-size: 0.75rem;
  height: 2rem;
  justify-content: center;
  padding: 0.5rem;
  width: 100%;
  ${({ theme }) => theme.mq.lg`
    width: 23.125rem;
  `}
`;

const StyledOrangeButton = styled(Button)`
  width: 95%;
  font-size: 0.75rem;
  height: 2rem;

  div {
    line-height: 1;
  }
  transition: all 0.3s;

  ${({ theme }) => theme.mq.lg`
  width: 23.125rem;
  `}

  :hover {
    background-color: ${({ theme }) => theme.themeColors.primary};
    color: ${({ theme }) => theme.themeColors.lightGreen3};
    opacity: 1;
  }
`;

const EmailEvailabilityLink = styled.a`
  width: 100%;
`;

const CallForBooking = ({ isMobile, phone, biz }) => {
  let link;
  let urlRegion;
  let urlLocality;
  let friendlyName;
  if (!isMobile) {
    urlRegion = biz.attributes.urlRegion;
    urlLocality = biz.attributes.urlLocality;
    friendlyName = biz.attributes.friendlyName;
    link = `/business/${urlRegion}/${urlLocality}/${friendlyName}`;
  } else {
    link = `tel:${phone}`;
  }

  if (!isMobile) {
    return (
      <Link href={link}>
        <StyledOrangeButton outlineType="outline">
          CALL PROVIDER TO BOOK AN APPOINTMENT
        </StyledOrangeButton>
      </Link>
    );
  }

  return (
    <EmailEvailabilityLink
      href={link}
      rel="noopener noreferrer"
      target="_blank"
      // onClick={(e) => e.stopPropagation()}
    >
      <StyledOrangeButton outlineType="outline">
        CALL PROVIDER TO BOOK AN APPOINTMENT
      </StyledOrangeButton>
    </EmailEvailabilityLink>
  );
};

CallForBooking.propTypes = {
  isMobile: PropTypes.bool,
  biz: PropTypes.shape({
    attributes: {
      urlRegion: PropTypes.string,
      urlLocality: PropTypes.string,
      friendlyName: PropTypes.string,
    },
  }),
  phone: PropTypes.string,
};

CallForBooking.defaultProps = {
  isMobile: false,
  biz: {},
  phone: "",
};

const NoBooking = () => {
  return (
    <EmptyAvailabilityPill>ONLINE BOOKING COMING SOON</EmptyAvailabilityPill>
  );
};

const NotClaimed = () => {
  return (
    <EmptyAvailabilityPill>CHECK BACK FOR ONLINE BOOKING</EmptyAvailabilityPill>
  );
};
function BusinessAvailabilityDisplay({
  biz,
  dateArr,
  groupedTimeslots,
  isActive,
  isCustomerUser,
  isAcceptingBooking,
  isDetail,
  isVendorUser,
  timeZoneId,
  mobileDateArr,
  list,
  isMobile,
  setShowChangeRoleModal,
  timePillLink,
  timePillQueryParams,
  showCustomerModalSetPath,
  updateSelectedDay,
}) {
  const ts = biz?.attributes?.firstAvailableTimeslot;
  let date = DateTime.fromISO(ts?.pill_display_start);
  const time = new Date(ts?.pill_display_start);
  let mins = time?.getMinutes().toString();
  let hours = time?.getHours();
  if (ts?.tz_datetime) {
    date = DateTime.fromISO(ts.tz_datetime, { setZone: true });
    hours = Math.floor(ts.minutes_since_midnight_start / 60).toString();
    mins = (ts.minutes_since_midnight_start % 60).toString();
  }
  let nextAvailableText = "COMING SOON";
  hours = `${hours % 12 || 12}:${mins.padStart(2, "0")} ${
    hours / 12 > 1 ? "PM" : "AM"
  }`;
  if (ts) {
    nextAvailableText = `IS ${date.month}/${date.day}/${date.year} @ ${hours}`;
  }
  const { vendorUserId, phone, businessType } = biz.attributes;
  const isVeterinary = businessType === "veterinary";
  return (
    <>
      <DesktopAvailabilityWrapper isVeterinary={isVeterinary}>
        {isAcceptingBooking && (
          <>
            {list.length < 1 && !isDetail ? (
              <EmptyAvailabilityButton
                onClick={(e) => {
                  e.preventDefault();
                  updateSelectedDay(date);
                }}
              >
                NEXT AVAILABLE DATE {nextAvailableText}
              </EmptyAvailabilityButton>
            ) : (
              <TimeSlotsContainer
                isClaimed={isAcceptingBooking}
                biz={biz}
                dateArr={dateArr}
                isActive={isActive}
                isCustomerUser={isCustomerUser}
                isDetail={isDetail}
                timeZoneId={timeZoneId}
                isVendorUser={isVendorUser}
                groupedTimeslots={groupedTimeslots}
                list={list}
                isMobile={isMobile}
                setShowChangeRoleModal={setShowChangeRoleModal}
                showCustomerModalSetPath={showCustomerModalSetPath}
                timePillLink={timePillLink}
                timePillQueryParams={timePillQueryParams}
                tsStartTime={date}
              />
            )}
          </>
        )}
        {(isVeterinary && <CallForBooking biz={biz} />) ||
          (vendorUserId && !isAcceptingBooking && <NoBooking />) ||
          (!vendorUserId && !isAcceptingBooking && <NotClaimed />)}
      </DesktopAvailabilityWrapper>
      <MobileAvailabilityWrapper isMobile>
        {isAcceptingBooking && (
          <TimeSlotsContainer
            dateArr={mobileDateArr}
            isClaimed={isAcceptingBooking}
            biz={biz}
            timeZoneId={timeZoneId}
            isActive={isActive}
            isCustomerUser={isCustomerUser}
            isDetail={isDetail}
            isVendorUser={isVendorUser}
            groupedTimeslots={groupedTimeslots}
            list={list}
            isMobile={isMobile}
            setShowChangeRoleModal={setShowChangeRoleModal}
            showCustomerModalSetPath={showCustomerModalSetPath}
            timePillLink={timePillLink}
            timePillQueryParams={timePillQueryParams}
            tsStartTime={date}
            updateSelectedDay={updateSelectedDay}
          />
        )}
        {(isVeterinary && <CallForBooking isMobile phone={phone} />) ||
          (vendorUserId && !isAcceptingBooking && <NoBooking />) ||
          (!vendorUserId && !isAcceptingBooking && <NotClaimed />)}
      </MobileAvailabilityWrapper>
    </>
  );
}

export default BusinessAvailabilityDisplay;

BusinessAvailabilityDisplay.propTypes = {
  biz: PropTypes.shape({
    attributes: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        businessName: PropTypes.string,
      }),
    ]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  list: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({}),
    ])
  ),
  dateArr: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({}),
    ])
  ),
  mobileDateArr: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({}),
    ])
  ),
  groupedTimeslots: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({}),
      ])
    ),
  ]),
  isActive: PropTypes.bool,
  timeZoneId: PropTypes.string,
  isCustomerUser: PropTypes.bool,
  isDetail: PropTypes.bool,
  isMobile: PropTypes.bool,
  isAcceptingBooking: PropTypes.bool,
  isVendorUser: PropTypes.bool,
  setShowChangeRoleModal: PropTypes.func,
  showCustomerModalSetPath: PropTypes.func,
  updateSelectedDay: PropTypes.func,
  timePillLink: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  timePillQueryParams: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
};

BusinessAvailabilityDisplay.defaultProps = {
  biz: {
    attributes: {
      businessName: undefined,
    },
    id: null,
  },
  list: [],
  dateArr: [],
  mobileDateArr: [],
  groupedTimeslots: [],
  isActive: false,
  timeZoneId: null,
  isCustomerUser: false,
  isDetail: false,
  isMobile: false,
  isAcceptingBooking: false,
  isVendorUser: false,
  setShowChangeRoleModal: () => {},
  showCustomerModalSetPath: () => {},
  updateSelectedDay: () => {},
  timePillLink: "/customer/booking/pets",
  timePillQueryParams: {},
};
